import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../layouts/LayoutDefault.jsx";
import axios from "axios";
import UserProfileBasicInfo from "../components/UserProfileBasicInfo";
import UserProfileListItems from "../components/loggedin/UserProfileListItems";
import UserEventsSection from "../components/loggedin/UserEventsSection";
import MainContainer from "../components/MainContainer";
import ContentContainer from "../components/ContentContainer";
import TitleTextSection from "../components/TitelTextSection";
import { FaPenToSquare } from "react-icons/fa6";
import { clearToken } from "../store";

const UserProfile = () => {
  const token = useSelector((state) => state.token);
  const dispatch = useDispatch();
  const apiUrl = import.meta.env.VITE_API_URL;
  const apiRoute = "userprofile";
  const apiEndpoint = `${apiUrl}${apiRoute}`;
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      // If token is not available, redirect user
      navigate("/404");
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(apiEndpoint, {
          withCredentials: true,
        });
        setUserProfile(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          dispatch(clearToken());
          window.location.href = "/login";
        } else {
          // Handle other types of errors
          setError(error.message);
        }
      }
    };

    fetchUserProfile();

    // Cleanup function to cancel the request if the component is unmounted
    return () => {
      // Cancel the request if it's still pending
    };
  }, [apiEndpoint, token, navigate, dispatch]);

  return (
    <Layout
      title="Profilsida"
      description="Din profilsida. Här finns allt som rör dina musikprojek och inlägg."
    >
      <MainContainer>
        {error && (
          <div className="alert alert-error my-4">
            <span>{error}</span>
          </div>
        )}
        {userProfile && (
          <>
            <UserEventsSection
              likedBands={userProfile.liked_bands}
              followedBands={userProfile.followed_bands}
              notifications={userProfile.notifications}
            />
            <UserProfileListItems
              itemType="band"
              items={userProfile.bands}
              title="Musikprojekt"
              createNewUrl="/userprofile/addmusicproject"
            ></UserProfileListItems>
            <UserProfileListItems
              itemType="post"
              items={userProfile.posts}
              title="Inlägg"
              createNewUrl="/userprofile/addpost"
            >
              {" "}
              <div className="grid grid-cols-6 gap-4 px-4 text-nowrap overflow-hidden">
                <div>
                  <p className="font-semibold">Typ</p>
                </div>
                <div>
                  <p className="font-semibold">Tillhör</p>
                </div>
                <div>
                  <p className="font-semibold">Title</p>
                </div>
                <div>
                  <p className="font-semibold">Ändra</p>
                </div>
                <div>
                  <p className="font-semibold">Besök</p>
                </div>
              </div>
            </UserProfileListItems>
            <UserProfileListItems
              itemType="event"
              items={userProfile.events || []}
              title="Evenemang"
              createNewUrl="/userprofile/addevent"
            ></UserProfileListItems>

            {Array.isArray(userProfile.organizers) &&
              userProfile.organizers.length > 0 && (
                <ContentContainer>
                  <div className="flex justify-between items-center mb-2">
                    <TitleTextSection title="Arrangör" />
                  </div>
                  <div className="hidden sm:grid grid-cols-12 px-4 mb-1">
                    <p className="col-span-11 text-xs uppercase tracking-wider text-base-content/50">
                      Namn
                    </p>
                    <p className="col-span-1 text-xs uppercase tracking-wider text-base-content/50 text-center">
                      Ändra
                    </p>
                  </div>
                  <div className="divide-y divide-base-content/10">
                    {userProfile.organizers.map((organizer) => (
                      <div
                        key={organizer.id || organizer.slug || organizer.name}
                        className="grid sm:grid-cols-12 grid-cols-4 items-center py-3 px-4"
                      >
                        <p className="col-span-3 sm:col-span-11 font-medium truncate">
                          {organizer.name || "-"}
                        </p>
                        <div className="col-span-1 flex justify-center">
                          <a
                            href={`/userprofile/editorganizer?slug=${organizer.slug}`}
                          >
                            <button className="btn btn-ghost btn-xs">
                              <FaPenToSquare size={15} />
                            </button>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </ContentContainer>
              )}
            <UserProfileBasicInfo profile={userProfile.profile} />
          </>
        )}
      </MainContainer>
    </Layout>
  );
};

export default UserProfile;
